import { useCallback, useEffect, useRef, useState } from "react";
import AdobeTextField from "../../AdobeAriaLib/AdobeTextField";
import { ISelected } from "../List/List";
import IconSearch from "../../../assets/icons/icon-search.svg";
import IconBack from "../../../assets/icons/icon-chevron-left-blue.svg";
import { Virtuoso } from "react-virtuoso";
import { httpPost } from "../../../modules/Backend";
import iconRight from "../../../assets/icons/icon-chevron-right.svg";
import { Button, Checkbox } from "react-aria-components";
import { modalSignal } from "./ModalContainer";
import GetSchema from "../entityComponents/Schema";
import warningIcon from "../../../assets/icons/icon-information.svg";
import { HttpStatusCode } from "axios";
import { PageSizes } from "../../../enums/PageSizes";
import LoadingButton from "../LoadingButton";

interface IMoveModal {
    entitiesToMove: ISelected[];
    entityName: string;
    onMoved?: Function;
}

interface IMoveLocation {
    id: string;
    locationId: string;
    name: string;
    canSelect: boolean;
}

export default function MoveModal({
    entitiesToMove,
    entityName,
    onMoved,
}: IMoveModal) {
    const schema = GetSchema(entityName);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
    const [depth, setDepth] = useState<number>(1);
    const pageSize = PageSizes.large;
    const [keySets, setKeySets] = useState<any>();
    const [data, setData] = useState<IMoveLocation[] | undefined>();
    const [endReached, setEndReached] = useState<boolean>(false);
    const [selected, setSelected] = useState<string | undefined>();
    const [locationsStack, setLocationStack] = useState<IMoveLocation[]>([]);
    const [moving, setMoving] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    const virtuoso = useRef<any>(null);

    const loadMore = useCallback(
        async (freshLoad: boolean, keySetsParam: any, currentSearchTerm: string) => {
            setLoading(true);

            const parentId = [...locationsStack].pop()?.id;

            const result = await httpPost(`api/${entityName}/moveLocations`, {
                depth,
                parentId,
                keySets: keySetsParam,
                take: pageSize,
                searchTerm: currentSearchTerm,
            });

            if (result.data) {
                setKeySets(result.data.keysets);

                setData((prevData) => {
                    const newItems = result.data.moveLocations;
                    if (freshLoad) {
                        // Scroll to top after replacing data
                        setTimeout(() => {
                            if (virtuoso.current) {
                                virtuoso.current.scrollToIndex({
                                    index: 0,
                                    align: "start",
                                    behavior: "auto",
                                });
                            }
                        }, 50);
                        return newItems;
                    }
                    return prevData ? [...prevData, ...newItems] : newItems;
                });

                setEndReached(result.data.moveLocations.length < pageSize);
            }

            setLoading(false);
        },
        [depth, locationsStack, entityName, pageSize]
    );

    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearchTerm(searchTerm), 250);
        return () => clearTimeout(id);
    }, [searchTerm]);

    useEffect(() => {
        loadMore(true, undefined, debouncedSearchTerm ?? "");
    }, [depth, debouncedSearchTerm, loadMore]);

    async function move() {
        setMoving(true);
        const result = await httpPost(`api/${entityName}/move`, {
            itemsToMove: entitiesToMove.map((x) => x.id),
            moveLocationId: selected,
        });
        if (result.status === HttpStatusCode.Ok) {
            modalSignal.value = undefined;
            if (onMoved) {
                onMoved(result.data.newLocation);
            }
        }
        setMoving(false);
    }

    function handleItemClick(item: IMoveLocation) {
        if (item && item.canSelect)
            setSelected(selected === item.locationId ? undefined : item.locationId);
        else {
            const newLocationStack = [...locationsStack];
            newLocationStack.push(item);
            setLocationStack(newLocationStack);
            setDepth((prev) => prev + 1);
            setSearchTerm("");
            setDebouncedSearchTerm("");
        }
    }

    function handleBackClick() {
        const newLocationStack = [...locationsStack];
        newLocationStack.pop();
        setLocationStack(newLocationStack);
        setDepth((prev) => prev - 1);
        setSelected(undefined);
    }

    return (
        <>
            <div className="display--flex justifyContent--space-between alignItems--center">
                <h2>Move {schema.displayName}</h2>
            </div>
            <div className="margin__top--xs">
                <AdobeTextField
                    value={searchTerm}
                    visibleLabel={false}
                    onChangeText={setSearchTerm}
                    disabled={false}
                    EnterKeyPress={() => { }}
                    icon={IconSearch}
                />
            </div>
            <div className="margin__top--xs">
                {locationsStack.length > 0 ? (
                    <Button
                        className="button button--text-only button--with-icon alignItems--center"
                        onPress={() => handleBackClick()}
                    >
                        <img src={IconBack} />
                        <p className="button-text margin__left--xxs">
                            {locationsStack.map((loc) => loc.name).join(" > ")}
                        </p>
                    </Button>
                ) : (
                    <p className="padding__top--xxxs">&nbsp;</p>
                )}
            </div>
            <div className="move-modal__wrapper margin__top--xs margin__bottom--xs">
                {!loading && (!data || data.length === 0) && (
                    <div className="display--flex alignItems--center flex__direction--column padding__top--xxxl">
                        <img src={warningIcon} />
                        <p className="margin__top--xxs">No Results Found</p>
                    </div>
                )}
                <Virtuoso
                    ref={virtuoso}
                    followOutput={false}
                    className="move-modal__list"
                    totalCount={data?.length ?? 0}
                    data={data ?? []}
                    endReached={endReached ? () => { } : () => loadMore(false, keySets, debouncedSearchTerm)}
                    itemContent={(index, item) => (
                        <div
                            key={item.id}
                            className="display--flex justifyContent--space-between margin__bottom--xs padding--xxs cursor__pointer"
                            onClick={() => { handleItemClick(item); }}
                        >
                            <div className="display--flex alignItems--center gap--xs">
                                <Checkbox
                                    slot="selection"
                                    isDisabled={!item.canSelect}
                                    onChange={() => {
                                        setSelected(
                                            selected === item.locationId ? undefined : item.locationId
                                        );
                                    }}
                                >
                                    <div className={`input-radio ${!item.canSelect ? "input-radio--disabled" : ""}`}>
                                        <div className={`input-radio__transition ${selected === item.locationId ? "input-radio--selected" : "input-radio--unselected"}`}>
                                        </div>
                                    </div>
                                </Checkbox>
                                <p>{item.name}</p>
                            </div>
                            {!item.canSelect && (
                                <div>
                                    <img src={iconRight} />
                                </div>
                            )}
                        </div>
                    )}
                />
                {loading && (
                    <div className="loaders loaders__panel height--full move-modal__loading-container" />
                )}
            </div>
            <div className="width--full display--flex justifyContent--center margin__top--xxs gap--xs">
                <Button
                    className="button button--secondary button--large"
                    onPress={() => (modalSignal.value = undefined)}
                >
                    <p className="button-text">Cancel</p>
                </Button>
                <LoadingButton
                    className="button button--large button--primary button--with-icon"
                    onPress={move}
                    buttonText="Confirm"
                    loadingText="Confirming"
                    isDisabled={selected === undefined || moving}
                    isLoading={moving}
                >
                    <></>
                </LoadingButton>
            </div>
        </>
    );
}
